/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   RunNowButton.tsx
 *     Feature         :   Run Now is acknowledged at once and cannot be sent twice (owner, 2026-10-31)
 *     Description     :   A job card's Run Now. While the job is on its way the button says what it is doing and
 *                         cannot be pressed. The server refuses a repeat on its own; this is what a person sees.
 *     Author          :   Peter Dobler
 * =========================================================================================
 */
import React from 'react';
import { Loader2, Play } from 'lucide-react';

import { AppTooltip, Button } from '../../components/ui';
import type { JobRunState } from '../../services/jobService';
import { RUN_STATE_HINT, RUN_STATE_LABEL } from './useTenantJobSchedule';

interface RunNowButtonProps {
  enabled: boolean;
  runState: JobRunState | null;
  onClick: () => void;
}

export function RunNowButton({ enabled, runState, onClick }: RunNowButtonProps) {
  const idleHint = enabled ? 'Run this job now for your organization' : 'This job is switched off';
  const icon = runState
    ? <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
    : <Play className="h-4 w-4" aria-hidden="true" />;

  return (
    <AppTooltip title={runState ? RUN_STATE_HINT[runState] : idleHint}>
      <Button
        variant="outline"
        size="sm"
        isFullWidth
        leftIcon={icon}
        onClick={onClick}
        disabled={!enabled || runState !== null}
      >
        {runState ? RUN_STATE_LABEL[runState] : 'Run Now'}
      </Button>
    </AppTooltip>
  );
}
